import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../contexts/TenantContext';
import useErrorHandler, { isBookingGone, isBookingStateChanged } from '../hooks/useErrorHandler';
import apiClient from '../services/apiClientService';
import { Routes } from '../routes/RouteConstants';

// Nothing left to decide on: the booking is gone (DEC-0023), or no longer PENDING (DEC-0026).
const isDecisionOutdated = (error) => isBookingGone(error) || isBookingStateChanged(error);

const useUpdateStatus = (targetRouteBeforeLogin = Routes.DASHBOARD) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const updateBookingStatus = async (bookingId, status) => {
    const url = `/v1/tenants/${tenantId}/bookings/${bookingId}/status`;
    return apiClient.patch(url, { status });
  };

  const mutation = useMutation({
    mutationFn: ({ bookingId, status }) => updateBookingStatus(bookingId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pendingBookings'] }),
    onError: (error) => {
      handleError(error, targetRouteBeforeLogin);
      // A booking gone or decided since the list was loaded must leave its pending card too.
      if (isDecisionOutdated(error)) {
        queryClient.invalidateQueries({ queryKey: ['pendingBookings'] });
      }
    },
  });

  // `onGone` runs when there is nothing left to decide on — the booking no longer exists
  // (DEC-0023), or is no longer PENDING (DEC-0026) — after the snackbar saying so.
  const updateStatus = (bookingId, status, onSuccess, onGone) => {
    mutation.mutate(
      { bookingId, status },
      {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (error) => {
          if (onGone && isDecisionOutdated(error)) {
            onGone();
          }
        },
      }
    );
  };

  return {
    updateStatus,
    // TanStack Query v5 calls it `isPending`; `mutation.isLoading` no longer exists.
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};

export default useUpdateStatus;
